/**
 * Centralized enums for the ShiftSage application
 * This file contains all magic string constants as enums for better type safety and maintainability
 */

// Theme-related enums
export enum Theme {
  LIGHT = 'light',
  DARK = 'dark'
}

// Overtime status enums
export enum OvertimeStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING_VP_APPROVAL = 'pending_vp_approval',
  PENDING_DH_APPROVAL = 'pending_dh_approval',
  PENDING_PM_APPROVAL = 'pending_pm_approval'
}

// User role enums
export enum UserRole {
  EMPLOYEE = 'employee',
  HR = 'hr',
  SUPER_ADMIN = 'super_admin',
  APP_OWNER = 'app_owner',
  DEPARTMENT_HEAD = 'department_head',
  SUPERINTENDENT = 'superintendent',
  PLANT_MANAGER = 'plant_manager'
}

// Time range enums
export enum TimeRange {
  SEVEN_DAYS = '7d',
  THIRTY_DAYS = '30d',
  NINETY_DAYS = '90d',
  THREE_SIXTY_FIVE_DAYS = '365d',
  ONE_YEAR = '1y',
  ALL = 'all',
  CALENDAR_MONTH = 'calendar_month',
  LAST_CALENDAR_MONTH = 'last_calendar_month'
}

// Day numbers for shift management (1=Sunday, 7=Saturday)
export enum DayNumber {
  SUNDAY = 1,
  MONDAY = 2,
  TUESDAY = 3,
  WEDNESDAY = 4,
  THURSDAY = 5,
  FRIDAY = 6,
  SATURDAY = 7
}

// Icon types for StatMetricCard
export enum StatIcon {
  HOURS = 'hours',
  PRODUCTIVITY = 'productivity',
  APPROVAL = 'approval',
  CHECK = 'check',
  ALERT = 'alert'
}

// Chart types
export enum ChartType {
  BAR = 'bar',
  LINE = 'line',
  PIE = 'pie',
  DONUT = 'donut',
  AREA = 'area'
}

// Notification types
export enum NotificationType {
  OVERTIME_APPROVED = 'overtime_approved',
  OVERTIME_REJECTED = 'overtime_rejected',
  OVERTIME_PENDING = 'overtime_pending',
  SYSTEM_UPDATE = 'system_update',
  GENERAL = 'general'
}

// Request types
export enum RequestType {
  OVERTIME = 'overtime',
  LEAVE = 'leave',
  SHIFT_CHANGE = 'shift_change'
}

// Company settings keys
export enum CompanySettingKey {
  MAX_OVERTIME_HOURS = 'max_overtime_hours',
  WEEK_START_DAY = 'week_start_day',
  COMPANY_NAME = 'company_name'
}

// Department comparison metrics
export enum DepartmentMetric {
  HOURS = 'hours',
  REQUESTS = 'requests',
  EMPLOYEES = 'employees',
  PRODUCTIVITY = 'productivity'
}

// Filter types
export enum FilterType {
  DEPARTMENT = 'department',
  STATUS = 'status',
  DATE_RANGE = 'date_range',
  EMPLOYEE = 'employee'
}

// Duty roster residency types
export enum DutyRosterResidencyType {
  RESIDENCE = 'Residence',
  NON_RESIDENCE = 'Non-Residence'
}

// Duty roster shift pattern types
export enum DutyRosterShiftPattern {
  SINGLE_SHIFT = 'single_shift',
  ROTATING = 'rotating'
}

// Duty roster date status types
export enum DutyRosterDateStatus {
  WORKING = 'working',
  OFF = 'off',
  UNASSIGNED = 'unassigned'
}

// Export utility functions for enum validation
export const isValidTheme = (value: string): value is Theme => {
  return Object.values(Theme).includes(value as Theme);
};

export const isValidOvertimeStatus = (value: string): value is OvertimeStatus => {
  return Object.values(OvertimeStatus).includes(value as OvertimeStatus);
};

export const isValidUserRole = (value: string): value is UserRole => {
  return Object.values(UserRole).includes(value as UserRole);
};

export const isValidTimeRange = (value: string): value is TimeRange => {
  return Object.values(TimeRange).includes(value as TimeRange);
};

export const isValidDayNumber = (value: number): value is DayNumber => {
  return Object.values(DayNumber).includes(value as DayNumber);
};

export const isValidDutyRosterResidencyType = (value: string): value is DutyRosterResidencyType => {
  return Object.values(DutyRosterResidencyType).includes(value as DutyRosterResidencyType);
};

export const isValidDutyRosterShiftPattern = (value: string): value is DutyRosterShiftPattern => {
  return Object.values(DutyRosterShiftPattern).includes(value as DutyRosterShiftPattern);
};

export const isValidDutyRosterDateStatus = (value: string): value is DutyRosterDateStatus => {
  return Object.values(DutyRosterDateStatus).includes(value as DutyRosterDateStatus);
};
